import React, { useEffect, useRef } from "react";
import { FileDrop as ReactFileDrop } from "react-file-drop";
import styled from "styled-components";

import { Text } from "../components/Text";
import { Button } from "../components/tailwind";

const normalizeAllowedExtensions = (extensions: string[]): string[] => {
  return extensions.map((extension: string): string => {
    const t = extension.trim().toLowerCase();
    return t.startsWith(".") ? t : `.${t}`;
  });
};

const extensionsToAcceptAttribute = (
  extensions: string[]
): string | undefined => {
  if (extensions.length === 0) {
    return undefined;
  }
  return normalizeAllowedExtensions(extensions).join(",");
};

const fileMatchesAllowedExtensions = (
  file: File,
  normalizedExtensions: string[]
): boolean => {
  if (normalizedExtensions.length === 0) {
    return true;
  }
  const lastDot = file.name.lastIndexOf(".");
  if (lastDot === -1) {
    return false;
  }
  const ext = file.name.slice(lastDot).toLowerCase();
  return normalizedExtensions.includes(ext);
};

export const StyledReactFileDrop = styled(ReactFileDrop)`
  .file-drop-target {
    border: 1px dashed lightgrey;
    border-radius: 10px;
    padding: 3em 1em;
    margin: 0 2em 1em;
  }
`;

type FileDropProps = {
  selectedFile: File | null;
  isInvalid: boolean;
  onChange: (file: File | null) => void;
  contentUnselected?: React.ReactNode;
  contentSelected?: React.ReactNode;
};

export const FileDrop: React.FC<FileDropProps> = ({
  selectedFile,
  isInvalid,
  onChange,
  contentUnselected,
  contentSelected,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="tw-text-center">
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const { files } = event.target;
          if (files.length > 0) {
            onChange(files.item(0));
          } else {
            onChange(null);
          }
          event.target.value = "";
        }}
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
      />
      {selectedFile ? (
        <div>
          {/* A file is selected */}
          <Text>Selected file: {selectedFile.name}</Text>
          {isInvalid && <Text color="red">Invalid file type.</Text>}
          {contentSelected}
          <Button
            type="button"
            data-testid="file-drop-remove-button"
            variant="secondary-alt"
            content={"Remove"}
            className="tw-mt-2 tw-ms-2"
            onClick={(): void => {
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
              onChange(null);
            }}
          />
        </div>
      ) : (
        <div data-testid="file-drop-target-wrapper">
          <StyledReactFileDrop
            onDrop={(files): void => {
              if (files.length > 0) {
                setTimeout(() => {
                  onChange(files.item(0));
                }, 200);
              } else {
                onChange(null);
              }
            }}
            onTargetClick={(): void => {
              fileInputRef.current?.click();
            }}
          >
            <Text as="h3">Drag your file to upload here.</Text>
            {contentUnselected}
          </StyledReactFileDrop>
          <Text color="grey" compact>
            or
          </Text>
          <Text>
            <Button
              type="button"
              variant="primary"
              content="Select file to upload"
              data-testid="file-drop-select-button"
              onClick={(): void => {
                fileInputRef.current?.click();
              }}
            />
          </Text>
        </div>
      )}
    </div>
  );
};

type MultiFileDropProps = {
  onAddFiles: (files: File[]) => void;
  content?: React.ReactNode;
  buttonOnly?: boolean;
};

export const MultiFileDrop: React.FC<MultiFileDropProps> = ({
  onAddFiles,
  content,
  buttonOnly = false,
}) => {
  const fileInputRef = useRef(null);

  return (
    <div
      className={buttonOnly ? "" : "tw-text-center"}
      data-testid="file-drop-target-wrapper"
    >
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const filelist = event.target.files;
          const files: File[] = [];
          for (let x = 0; x < filelist.length; x++) {
            files.push(filelist.item(x));
          }
          onAddFiles(files);
        }}
        ref={fileInputRef}
        type="file"
        className="tw-hidden"
        multiple
      />
      {!buttonOnly && (
        <>
          <StyledReactFileDrop
            onDrop={(filelist): void => {
              const files: File[] = [];
              for (let x = 0; x < filelist.length; x++) {
                files.push(filelist.item(x));
              }
              onAddFiles(files);
            }}
            onTargetClick={(): void => {
              fileInputRef.current.click();
            }}
          >
            <Text as="h3">Drag your file(s) to upload here.</Text>
            {content}
          </StyledReactFileDrop>
          <Text color="grey" compact>
            or
          </Text>
        </>
      )}
      <Text>
        <Button
          type="button"
          variant="primary"
          content="Select file(s) to upload"
          data-testid="file-drop-select-button"
          onClick={(): void => {
            fileInputRef.current.click();
          }}
        />
      </Text>
    </div>
  );
};

type FileDropContainerProps = {
  selectedFile: File | null;
  isInvalid: boolean;
  children: React.ReactNode;
  onChange: (file: File | null) => void;
  allowedFileExtensions?: string[];
  placeholderText?: string;
};

export const FileDropContainer: React.FC<FileDropContainerProps> = ({
  selectedFile,
  isInvalid,
  children,
  onChange,
  allowedFileExtensions = [],
  placeholderText = "Attach file",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const normalizedExtensions = normalizeAllowedExtensions(
    allowedFileExtensions
  );
  const acceptAttribute = extensionsToAcceptAttribute(allowedFileExtensions);

  useEffect(() => {
    if (!selectedFile && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [selectedFile]);

  return (
    <div>
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const { files } = event.target;
          if (files.length > 0) {
            const file = files.item(0);
            if (fileMatchesAllowedExtensions(file, normalizedExtensions)) {
              onChange(file);
            } else {
              onChange(null);
              event.target.value = "";
            }
          } else {
            onChange(null);
          }
        }}
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        accept={acceptAttribute}
      />
      <ReactFileDrop
        onDrop={(files): void => {
          if (files.length > 0) {
            const file = files.item(0);
            setTimeout(() => {
              if (fileMatchesAllowedExtensions(file, normalizedExtensions)) {
                onChange(file);
              }
            }, 200);
          } else {
            onChange(null);
          }
        }}
      >
        <div className="tw-flex tw-items-center tw-gap-1">
          <Button
            onClick={(): void => {
              fileInputRef.current.click();
            }}
            type="button"
            className="!tw-border-0 !tw-p-x-1 !tw-pt-1 tw-flex tw-justify-center"
            variant="secondary-alt"
            data-testid="file-drop-select-button"
            compact="very"
          >
            <i className="material-icons tw-text-md">insert_photo</i>
          </Button>
          <div className="tw-flex tw-items-center tw-gap-0.5 tw-leading-none tw-text-secondary">
            {!isInvalid && selectedFile ? (
              <>
                {selectedFile.name}

                <i
                  onClick={() => {
                    onChange(null);
                  }}
                  className="material-icons hover:tw-cursor-pointer tw-mt-[2px] tw-text-[20px] tw-font-thin"
                >
                  close_small
                </i>
              </>
            ) : (
              placeholderText
            )}
          </div>
        </div>
        {children}
      </ReactFileDrop>
    </div>
  );
};
